import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import discgoLogo from 'figma:asset/70d8fe074a29674a16552ebcf7b0d662532aa1b0.png';
import { LogOut } from 'lucide-react';

const PLAYER_WEB_URL = import.meta.env.VITE_PLAYER_WEB_URL || 'http://localhost:5000';
const OWNER_WEB_URL = import.meta.env.VITE_OWNER_WEB_URL || 'http://localhost:5001';

export default function RoleSelector() {
  const { roles, setActiveRole, displayName, signOut } = useAuth();

  const handleRoleSelection = async (role: 'player' | 'owner') => {
    await setActiveRole(role);

    const { data: { session } } = await supabase.auth.getSession();

    if (session) {
      const targetUrl = role === 'player' ? PLAYER_WEB_URL : OWNER_WEB_URL;
      const redirectUrl = `${targetUrl}#access_token=${session.access_token}&refresh_token=${session.refresh_token}&expires_at=${session.expires_at}&expires_in=${session.expires_in}&token_type=${session.token_type}`;
      window.location.href = redirectUrl;
    } else {
      if (role === 'player') {
        window.location.href = PLAYER_WEB_URL;
      } else if (role === 'owner') {
        window.location.href = OWNER_WEB_URL;
      }
    }
  };

  const hasPlayerRole = roles.includes('player');
  const hasOwnerRole = roles.includes('owner') || roles.includes('admin');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img
            src={discgoLogo}
            alt="Discgo"
            className="h-16 w-auto"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 pb-4">
            <h2 className="text-2xl text-center text-foreground font-bold">
              welcome back, {displayName}!
            </h2>
            <p className="text-center text-muted-foreground mt-2 lowercase">
              choose how you'd like to continue
            </p>
          </div>

          <div className="p-6 pt-4 space-y-3">
            {hasPlayerRole && (
              <button
                onClick={() => handleRoleSelection('player')}
                className="w-full p-4 border rounded-full hover:bg-accent transition-all group text-left"
                style={{ borderColor: 'var(--border)' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--primary)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
              >
                <div className="px-2">
                  <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors lowercase">
                    continue as player
                  </h3>
                  <p className="text-muted-foreground text-sm lowercase mt-0.5">
                    browse courses and book tee times
                  </p>
                </div>
              </button>
            )}

            {hasOwnerRole && (
              <button
                onClick={() => handleRoleSelection('owner')}
                className="w-full p-4 border rounded-full hover:bg-accent transition-all group text-left"
                style={{ borderColor: 'var(--border)' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--primary)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
              >
                <div className="px-2">
                  <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors lowercase">
                    continue as course owner
                  </h3>
                  <p className="text-muted-foreground text-sm lowercase mt-0.5">
                    manage your courses and bookings
                  </p>
                </div>
              </button>
            )}

            <div className="pt-6 border-t border-gray-100 text-center">
              <button
                onClick={() => signOut()}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors lowercase"
              >
                <LogOut className="w-4 h-4" />
                sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
